// ============================================================================
// Model picker — model selection dropdown
// ============================================================================
// Depends on: $ (dom.js), haptic (shared.js global), send (main.js),
// state (state.js), renderPastePreview (main.js, passed via init).

import { $, modelPickerBtn, mpOverlay, mpClose, mpCurrent, mpSearch, mpList } from "./dom.js";
import { state } from "./state.js";
import { wireModal, openModal, closeModal } from "./modal.js";

let modelFamilies = [];
let configOptions = [];
let modelConfigId = null;
let renderPastePreview = null; // set by initModelPicker
let sendFn = null; // set by initModelPicker

export async function loadModels() {
  try {
    const res = await fetch("/api/models");
    if (!res.ok) return;
    const data = await res.json();
    modelFamilies = data.families || [];
    if (!state.sessionActive) renderModelPicker();
  } catch { /* ignore */ }
}

export function findModelCost(modelUid) {
  for (const fam of modelFamilies) {
    for (const v of (fam.variants || [])) {
      if (v.model_uid === modelUid) return v;
    }
  }
  return null;
}

export function findFamilyBySlug(slug) {
  return modelFamilies.find((f) => f.slug === slug || f.aliases?.includes(slug));
}

export function setConfigOptions(opts) {
  configOptions = opts;
}

export function getModelConfigId() { return modelConfigId; }

export function restoreSavedModel(send) {
  const saved = localStorage.getItem("devin-selected-model");
  if (!saved) return;
  const modelConfig = configOptions.find(
    (o) => o.category === "model" || o.category === "model_config" || o.id.includes("model")
  );
  if (!modelConfig || !modelConfigId) return;
  const currentValue = modelConfig.currentValue;
  if (saved === currentValue) return;
  const opt = (modelConfig.options || []).find((o) => o.value === saved);
  if (!opt) return;
  const value = opt.value;
  if (!value) return;
  send({ type: "set_config_option", configId: modelConfigId, value });
  mpCurrent.textContent = opt.name || opt.value;
}

export function renderModelPicker() {
  mpList.innerHTML = "";

  const modelConfig = configOptions.find(
    (o) => o.category === "model" || o.category === "model_config" || o.id.includes("model")
  );

  if (modelConfig && modelConfig.type === "select") {
    modelConfigId = modelConfig.id;
    const currentValue = modelConfig.currentValue;
    const options = modelConfig.options || [];
    const grouped = new Map();
    const ungrouped = [];
    for (const opt of options) {
      const cost = findModelCost(opt.value);
      const fam = cost ? modelFamilies.find((f) => f.variants?.some((v) => v.model_uid === opt.value)) : null;
      if (fam) {
        if (!grouped.has(fam.family_label)) grouped.set(fam.family_label, []);
        grouped.get(fam.family_label).push({ opt, cost });
      } else {
        ungrouped.push({ opt, cost });
      }
    }
    for (const [famLabel, items] of grouped) {
      const g = document.createElement("div");
      g.className = "mpd-group";
      g.textContent = famLabel;
      mpList.appendChild(g);
      for (const { opt, cost } of items) {
        mpList.appendChild(renderModelItem(opt, currentValue, cost));
      }
    }
    if (ungrouped.length) {
      const g = document.createElement("div");
      g.className = "mpd-group";
      g.textContent = "Other";
      mpList.appendChild(g);
      for (const { opt, cost } of ungrouped) {
        mpList.appendChild(renderModelItem(opt, currentValue, cost));
      }
    }
    const currentOpt = options.find((o) => o.value === currentValue);
    mpCurrent.textContent = currentOpt?.name || currentValue || "Adaptive";
  } else {
    modelConfigId = null;
    for (const fam of modelFamilies) {
      const g = document.createElement("div");
      g.className = "mpd-group";
      g.textContent = fam.family_label;
      mpList.appendChild(g);
      for (const v of (fam.variants || [])) {
        const item = document.createElement("div");
        item.className = "mpd-item";
        item.innerHTML = `<div class="mpd-label"><span>${v.label}</span></div><div class="mpd-cost">${v.cost_summary || ""}</div>`;
        mpList.appendChild(item);
      }
    }
  }
}

function renderModelItem(opt, currentValue, cost) {
  const item = document.createElement("div");
  item.className = "mpd-item";
  const isSelected = opt.value === currentValue;
  if (isSelected) item.classList.add("selected");
  const label = document.createElement("div");
  label.className = "mpd-label";
  if (isSelected) {
    const check = document.createElement("span");
    check.className = "mpd-check";
    check.textContent = "✓";
    label.appendChild(check);
  }
  const name = document.createElement("span");
  name.textContent = opt.name || opt.value;
  label.appendChild(name);
  if (cost) {
    const tier = document.createElement("span");
    tier.className = "mpd-tier " + (cost.cost_tier || "").toLowerCase().replace(/\s/g, "");
    tier.textContent = cost.cost_tier || "";
    label.appendChild(tier);
  }
  item.appendChild(label);
  if (cost && cost.cost_summary) {
    const c = document.createElement("div");
    c.className = "mpd-cost";
    c.textContent = cost.cost_summary;
    item.appendChild(c);
  }
  item.onclick = () => {
    haptic("tap");
    if (modelConfigId && !isSelected) {
      if (sendFn) sendFn({ type: "set_config_option", configId: modelConfigId, value: opt.value });
      mpCurrent.textContent = opt.name || opt.value;
      localStorage.setItem("devin-selected-model", opt.value || "");
      if (state.pendingImages?.length && renderPastePreview) renderPastePreview();
    }
    closeModelDropdown();
  };
  return item;
}

export function toggleModelDropdown() {
  const isOpen = !mpOverlay.hidden;
  if (isOpen) {
    closeModelDropdown();
  } else {
    renderModelPicker();
    mpOverlay.hidden = false;
    mpSearch.value = "";
    filterModels("");
    setTimeout(() => mpSearch.focus(), 50);
  }
}

export function closeModelDropdown() { mpOverlay.hidden = true; }

function filterModels(query) {
  const q = query.toLowerCase();
  for (const el of mpList.children) {
    if (el.classList.contains("mpd-group")) continue;
    el.style.display = el.textContent.toLowerCase().includes(q) ? "" : "none";
  }
  let prevGroup = null;
  for (const el of mpList.children) {
    if (el.classList.contains("mpd-group")) {
      if (prevGroup) prevGroup.style.display = "none";
      prevGroup = el;
    } else if (el.style.display !== "none") {
      if (prevGroup) prevGroup.style.display = "";
      prevGroup = null;
    }
  }
  if (prevGroup) prevGroup.style.display = "none";
}

/**
 * Initialize the model picker event handlers.
 * @param {function} sendFn - the WebSocket send function
 * @param {function} renderPastePreviewFn - paste preview renderer
 */
export function initModelPicker(send, renderPastePreviewFn) {
  sendFn = send;
  renderPastePreview = renderPastePreviewFn;

  if (modelPickerBtn) {
    modelPickerBtn.addEventListener("click", () => { haptic("tap"); toggleModelDropdown(); });
  }
  if (mpClose) {
    mpClose.addEventListener("click", () => { haptic("tap"); closeModelDropdown(); });
  }
  if (mpOverlay) {
    mpOverlay.addEventListener("click", (e) => {
      if (e.target === mpOverlay) closeModelDropdown();
    });
  }
  if (mpSearch) {
    mpSearch.addEventListener("input", () => filterModels(mpSearch.value));
  }
}
